const TRACKING_SEARCH_PARAM_NAMES = new Set([
  '_ga',
  '_gl',
  '_hsenc',
  '_hsmi',
  'dclid',
  'fbclid',
  'gad_campaignid',
  'gad_source',
  'gbraid',
  'gclid',
  'igshid',
  'mc_cid',
  'mc_eid',
  'msclkid',
  'srsltid',
  'wbraid'
])

const TRACKING_SEARCH_PARAM_PREFIXES = ['utm_']
const PRODUCT_COLOR_DETAIL_PATH_PATTERN = /^\/(?:(?:fr|es|pt|ja|ko|de|it|zh)\/)?(?:boots|ankle-boots|pumps|sandals|bottines|bottes|escarpins|sandales)\/[^/?#]+$/

export function normalizePathname(pathname: string) {
  if (!pathname) return '/'
  return pathname.replace(/\/+$/, '') || '/'
}

export function isTrackingSearchParam(name: string) {
  const normalizedName = name.toLowerCase()
  return (
    TRACKING_SEARCH_PARAM_NAMES.has(normalizedName) ||
    TRACKING_SEARCH_PARAM_PREFIXES.some((prefix) => normalizedName.startsWith(prefix))
  )
}

export function getSearchParamValue(searchParams: URLSearchParams, targetName: string) {
  const normalizedTargetName = targetName.toLowerCase()

  for (const [name, value] of searchParams.entries()) {
    if (name.toLowerCase() === normalizedTargetName) {
      return value
    }
  }

  return null
}

export function deleteSearchParam(searchParams: URLSearchParams, targetName: string) {
  const normalizedTargetName = targetName.toLowerCase()
  const namesToDelete = new Set<string>()

  for (const name of searchParams.keys()) {
    if (name.toLowerCase() === normalizedTargetName) {
      namesToDelete.add(name)
    }
  }

  for (const name of namesToDelete) {
    searchParams.delete(name)
  }
}

export function getCanonicalSearchParams(pathname: string, searchParams: URLSearchParams) {
  const normalizedPathname = normalizePathname(pathname)
  const nextSearchParams = new URLSearchParams()
  const isProductColorDetailPath = PRODUCT_COLOR_DETAIL_PATH_PATTERN.test(normalizedPathname)

  for (const [name, value] of searchParams.entries()) {
    if (isTrackingSearchParam(name)) continue
    if (isProductColorDetailPath && name.toLowerCase() === 'color') continue
    nextSearchParams.append(name, value)
  }

  return nextSearchParams
}

export function formatSearchParams(searchParams: URLSearchParams) {
  const search = searchParams.toString()
  return search ? `?${search}` : ''
}

export function formatCanonicalRedirectTarget(targetPath: string, searchParams: URLSearchParams, hadOriginalSearch = false) {
  const search = formatSearchParams(searchParams)
  if (search) return `${targetPath}${search}`
  return hadOriginalSearch ? `${targetPath}?` : targetPath
}

export const LANDING_HANDOFF_COOKIE = 'kdk_landing'
const LANDING_HANDOFF_MAX_VALUE_LENGTH = 120

/**
 * Tracking params removed by the canonical redirect, re-encoded for a short-lived
 * first-party handoff cookie: utm_* keep their (truncated) value, click ids are
 * reduced to a presence flag so no identifier is stored.
 */
export function getLandingHandoff(searchParams: URLSearchParams) {
  const handoff = new URLSearchParams()

  for (const [name, value] of searchParams.entries()) {
    const key = name.toLowerCase()
    if (!isTrackingSearchParam(key)) continue
    if (TRACKING_SEARCH_PARAM_PREFIXES.some(prefix => key.startsWith(prefix))) {
      if (value) handoff.set(key, value.slice(0, LANDING_HANDOFF_MAX_VALUE_LENGTH))
    } else if (key.endsWith('clid') || key === 'igshid') {
      handoff.set(key, '1')
    }
  }

  return handoff.toString()
}
